import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePolling } from "@/hooks/usePolling";
import { activas, misEtapas, etapaActual, numeroEtapa, estadoChofer, misArchivos, hoyYmd } from "@/lib/chofer";
import { CabeceraCarga } from "@/components/chofer/CabeceraCarga";
import { TripRecap } from "@/components/chofer/TripRecap";
import { EtapaActual } from "@/components/chofer/EtapaActual";
import { EtapaRevision } from "@/components/chofer/EtapaRevision";
import { Completado } from "@/components/chofer/Completado";
import { EtaDialog, LiberarDialog, ArchivosDialog } from "@/components/chofer/Dialogos";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Ban } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): la carga del chofer, en inglés y por etapas: Trip Recap → una pantalla por etapa → Completed.
// «Back to previous step» y «Return to current step» solo cambian lo que se ve (modo revisión): no tocan el estado,
// no borran lo escrito y no repiten ninguna salida. Lo escrito en cada etapa vive en `forms` mientras la pantalla está abierta.
function BotonNav({ onClick, children, testid, derecha = false }) {
  return (
    <Button type="button" variant="outline" data-testid={testid} onClick={onClick} className="w-full h-[52px] text-[16px] font-bold text-navy-2 bg-white">
      {!derecha && <ArrowLeft className="h-5 w-5" />}{children}{derecha && <ArrowRight className="h-5 w-5" />}
    </Button>
  );
}
export default function ChoferCargaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id;
  const [carga, setCarga] = useState(null);
  const [error, setError] = useState("");
  const [vista, setVista] = useState(null); // null = la que corresponde | "recap" | clave de etapa
  const [forms, setForms] = useState({});
  const [dialog, setDialog] = useState(null); // "eta" | "liberar" | "files"
  const [empezando, setEmpezando] = useState(false);
  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/chofer/cargas/${id}`);
      setCarga(data);
    } catch (e) { setError(apiError(e)); }
  }, [id]);
  usePolling(cargar, 30000);
  const empezar = async () => {
    setEmpezando(true);
    try {
      const { data } = await api.post(`/chofer/cargas/${id}/iniciar`);
      setCarga(data);
      setVista(null);
      toast.success(t("Trip started"));
    } catch (e) { toast.error(apiError(e)); } finally { setEmpezando(false); }
  };
  if (error) {
    return (
      <div className="max-w-md mx-auto p-4">
        <p data-testid="chofer-detalle-error" className="text-danger font-medium mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate("/chofer")}><ArrowLeft className="h-4 w-4" />{t(" Back to loads")}</Button>
      </div>
    );
  }
  if (!carga) return <div className="max-w-md mx-auto p-4 space-y-3"><Skeleton className="h-28" /><Skeleton className="h-64" /></div>;
  const ets = activas(carga);
  const mias = misEtapas(carga, uid);
  const act = etapaActual(carga);
  const est = estadoChofer(carga, uid);
  const cancelada = carga.estado === "cancelada";
  const iniciado = !!carga.viaje_inicio_at;
  const terminada = !act;
  const miTurno = !!carga.es_mi_turno;
  const nombre = user?.nombre || "";
  const etapaDe = (c) => ets.find((e) => e.clave === c);
  const defaults = (e) => {
    const v = carga.eta_vigente;
    const entrega = etapaDe("delivery");
    return {
      chassis: e.chassis_reportado || "", contenedor: e.contenedor_reportado || "", espacio: e.espacio || "",
      etaFecha: v?.eta_fecha || entrega?.fecha || hoyYmd(), etaHora: v?.eta_hora || "",
    };
  };
  const formDe = (e) => forms[e.clave] || defaults(e);
  const setForm = (e) => (patch) => setForms((p) => ({ ...p, [e.clave]: { ...(p[e.clave] || defaults(e)), ...patch } }));
  // Qué se muestra: si el chofer no eligió otra cosa, la pantalla que corresponde al momento del viaje.
  let v = vista;
  if (cancelada) v = "recap";
  else if (!v) {
    if (!iniciado) v = "recap";
    else if (terminada || est.k === "my_part_done") v = "completado";
    else if (miTurno) v = act.clave;
    else v = "espera";
  }
  const irAtras = (clave) => {
    const i = mias.findIndex((e) => e.clave === clave);
    setVista(i > 0 ? mias[i - 1].clave : "recap");
  };
  const volverActual = () => setVista(null);
  const hayPendientes = !cancelada && iniciado && !terminada && ets.some((e) => e.chofer_id === uid && !e.salida_at);
  let cuerpo = null;
  if (v === "recap") {
    cuerpo = (
      <TripRecap
        carga={carga} uid={uid} nombreChofer={nombre} miTurno={miTurno} iniciado={iniciado} terminada={terminada} soloLectura={cancelada}
        empezando={empezando} onEmpezar={empezar} onVolver={volverActual} onEtapa={(c) => setVista(c)}
      />
    );
  } else if (v === "completado") {
    const ultimaMia = [...mias].reverse().find((e) => e.salida_at) || mias[mias.length - 1];
    cuerpo = (
      <>
        <BotonNav testid="atras-btn" onClick={() => setVista(ultimaMia.clave)}>{t("Back to previous step")}</BotonNav>
        <Completado carga={carga} uid={uid} nombreChofer={nombre} completa={terminada} onFiles={() => setDialog("files")} onEtapa={(c) => setVista(c)} />
      </>
    );
  } else if (v === "espera") {
    cuerpo = (
      <>
        <section className="bg-white border border-line rounded-[14px] p-4" data-testid="chofer-espera">
          <p className="eyebrow text-navy-2">{t("Waiting · Driver {nombre}", { nombre })}</p>
          <p className="mt-1 text-[16px] text-ink">{t("The previous steps are with another driver. Your next step opens here when it is your turn.")}</p>
        </section>
        <BotonNav testid="ver-recap-btn" derecha onClick={() => setVista("recap")}>{t("View trip recap")}</BotonNav>
      </>
    );
  } else {
    const e = mias.find((x) => x.clave === v) || mias.find((x) => !x.salida_at) || mias[0];
    const esActual = !!act && e.clave === act.clave && miTurno && iniciado;
    cuerpo = (
      <>
        <BotonNav testid="atras-btn" onClick={() => irAtras(e.clave)}>{mias[0]?.clave === e.clave ? t("Back to trip recap") : t("Back to previous step")}</BotonNav>
        {!esActual && !cancelada && (act ? <BotonNav testid="volver-actual-btn" derecha onClick={volverActual}>{t("Return to current step")}</BotonNav> : <BotonNav testid="volver-actual-btn" derecha onClick={volverActual}>{t("Back to trip summary")}</BotonNav>)}
        {esActual ? (
          <EtapaActual
            carga={carga} e={e} numero={numeroEtapa(carga, e.clave, uid)} uid={uid} nombreChofer={nombre}
            form={formDe(e)} setForm={setForm(e)} onCarga={setCarga} onSalio={() => setVista(null)}
            onEta={() => setDialog("eta")} onFiles={() => setDialog("files")} onRecap={() => setVista("recap")}
          />
        ) : (
          <EtapaRevision carga={carga} e={e} uid={uid} onCarga={setCarga} />
        )}
      </>
    );
  }
  return (
    <div className="bg-paper">
      <CabeceraCarga carga={carga} nombreChofer={nombre} />
      <main className="max-w-md mx-auto p-4 space-y-3" data-testid="chofer-detalle-main">
        {cancelada && (
          <div className="rounded-xl border-2 border-danger bg-[#FBEDEC] p-3 flex items-start gap-2" data-testid="chofer-cancelada">
            <Ban className="h-5 w-5 text-danger shrink-0 mt-0.5" />
            <p className="text-[15px] font-semibold text-danger">{t("This load was cancelled. Contact dispatch.")}</p>
          </div>
        )}
        {cuerpo}
        {hayPendientes && (
          <Button type="button" variant="outline" data-testid="chofer-liberar-btn" onClick={() => setDialog("liberar")} className="w-full h-12 text-[15px] text-danger">{t("I can’t finish this trip")}</Button>
        )}
      </main>
      {dialog === "eta" && (
        <EtaDialog cargaId={carga.id} vigente={carga.eta_vigente} zonaLabel={etapaDe("delivery")?.zona_horaria || "America/Chicago"} onClose={() => setDialog(null)} onCarga={setCarga} />
      )}
      {dialog === "liberar" && <LiberarDialog cargaId={carga.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); navigate("/chofer"); }} />}
      {dialog === "files" && (
        <ArchivosDialog archivos={misArchivos(carga, uid)} nombreEtapa={(c) => etapaDe(c)?.nombre || c} zonaDe={(c) => etapaDe(c)?.zona_horaria} onClose={() => setDialog(null)} />
      )}
    </div>
  );
}
